import React from "react"
import PropTypes from "prop-types"
import { Container, Title } from "@bw/bits"
import styled, { css } from "styled-components"
import { breakpoints } from "../../../theme"

const StyledSection = styled.section`
  --offset: 80px;
  position: relative;
  margin: var(--offset) 0;

  ${props =>
    props.$hasImage &&
    css`
      margin: 0;
      padding: var(--offset) 0;

      /*optimize readability on mobiles*/
      @media (max-width: ${breakpoints.medium - 1}px) {
        background-color: var(
          --${props => (props.$negativeTextColor ? "text-color" : "negative-text-color")}
        );
        .gatsby-image-wrapper {
          opacity: 0.3;
        }
      }

      @media (min-width: ${breakpoints.medium}px) {
        text-shadow: 0 0 10px
          var(
            --${props => (props.$negativeTextColor ? "text-color" : "negative-text-color")}
          );
        padding: calc(var(--offset) * 2) 0;
      }
    `}

  ${props =>
    props.backgroundVariant &&
    css`
      margin-top: 0;
      padding: var(--offset) 0 60px;

      &:before {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
        max-height: var(--section-background-variant-max-height, 480px);
        background: var(--section-background-variant-background);

        clip-path: var(--section-background-variant-clip-path-mobile, none);

        @media (min-width: ${breakpoints.medium}px) {
          clip-path: var(--section-background-variant-clip-path-desktop, none);
        }
      }
    `}

  .title {
    margin-bottom: 30px;
  
    @media (min-width: ${breakpoints.medium}px) {
      margin-bottom: 45px;
    }

    ${props =>
      props.$offset &&
      css`

      +.section--background-variant {margin-top: calc(-1 * var(--offset));
    `}
  }
`
const ButtonWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  transform: translateY(50%);
  justify-content: ${props =>
    props.$justify === "center" ? "center" : `flex-${props.$justify}`};
  ${props =>
    props.$offset &&
    css`
      margin-bottom: calc(-1 * var(--offset));
    `}
`

const Grid = styled.div`
  @media (min-width: ${breakpoints.medium}px) {
    display: grid;
    grid-template-columns: 2fr 3fr;
  }

  @media (min-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 2fr;
  }
`

const Image = styled.div`
  position: absolute;
  overflow: hidden;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;

  .gatsby-image-wrapper {
    height: 100%;
  }

  img {
    object-position: ${props => props.focus};
  }
`

const Section = ({
  suptitle,
  title,
  subtitle,
  fullWidth,
  containerSize,
  children,
  button,
  buttonPosition,
  idAttribute,
  classAttribute,
  titleOnSide,
  backgroundVariant,
  isFollowedByModuleWithBackgroundVariant,
  negativeTextColor,
  image,
  focus,
}) => {
  let className = "section"

  if (backgroundVariant) {
    className += " section--background-variant"
  }

  if (classAttribute) {
    className += ` ${classAttribute}`
  }

  if (negativeTextColor) {
    className += " negativeTextColor"
  }

  if (titleOnSide) {
    className += ` section--title-on-side`

    return (
      <StyledSection
        backgroundVariant={backgroundVariant}
        $hasImage={image}
        $negativeTextColor={negativeTextColor}
        $offset={isFollowedByModuleWithBackgroundVariant}
        className={className}
        id={idAttribute}
      >
        {image && <Image focus={focus}>{image}</Image>}
        <Container size={containerSize}>
          <Grid>
            {(suptitle || title) && (
              <Title {...{ suptitle, title, subtitle }} />
            )}
            <div>
              {children}

              {button && (
                <Container size={containerSize} className="section__button">
                  <ButtonWrapper
                    $justify={buttonPosition}
                    $offset={isFollowedByModuleWithBackgroundVariant}
                  >
                    {button}
                  </ButtonWrapper>
                </Container>
              )}
            </div>
          </Grid>
        </Container>
      </StyledSection>
    )
  }

  const content = fullWidth ? (
    children
  ) : (
    <Container size={containerSize}>{children}</Container>
  )

  return (
    <StyledSection
      backgroundVariant={backgroundVariant}
      $hasImage={image}
      $negativeTextColor={negativeTextColor}
      $offset={isFollowedByModuleWithBackgroundVariant}
      className={className}
      id={idAttribute}
    >
      {image && <Image focus={focus}>{image}</Image>}
      {(suptitle || title || subtitle) && (
        <Container size={containerSize}>
          <Title {...{ suptitle, title, subtitle }} />
        </Container>
      )}
      {content}
      {button && (
        <Container size={containerSize} className="section__button">
          <ButtonWrapper
            $justify={buttonPosition}
            $offset={isFollowedByModuleWithBackgroundVariant}
          >
            {button}
          </ButtonWrapper>
        </Container>
      )}
    </StyledSection>
  )
}

Section.propTypes = {
  suptitle: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  fullWidth: PropTypes.bool,
  titleOnSide: PropTypes.bool,
  backgroundVariant: PropTypes.bool,
  negativeTextColor: PropTypes.bool,
  image: PropTypes.element,
  focus: PropTypes.string,
  button: PropTypes.element,
  buttonPosition: PropTypes.oneOf(["start", "center", "end"]),
  isFollowedByModuleWithBackgroundVariant: PropTypes.bool,
  containerSize: PropTypes.oneOf(["small", "medium", "large"]),
  idAttribute: PropTypes.string,
  classAttribute: PropTypes.string,
}

Section.defaultProps = {
  buttonPosition: "center",
  containerSize: "medium",
  isFollowedByModuleWithBackgroundVariant: false,
}

export { Section }
